/**
 * 월 정산 엑셀 내보내기 — 서버 응답 → 시트별 2차원 배열(aoa)·열 너비·숫자 서식 위치·시트 이름 (순수 함수).
 *
 * 계획: ~/md/plan/콜봇-월정산-정산시작일-엑셀내보내기-계획.md 4-5절(엑셀 파일 구성)·6절(시험).
 * 서버 계약: ~/md/back/콜봇-월정산-정산시작일-엑셀내보내기-call-api요청.md 요청 계약 9절(응답 companies[].summary·detail).
 *
 *  - 모든 시트 1–4행 머리(제목 · 내보낸 시각(KST) · 내보내기 ID · 담은 내용), 5행 비움, 6행부터 표.
 *  - 요약 시트 '요약'은 회사가 하나여도 둔다. 열은 담은 내용에 따라 바뀐다(정산서 → 공급가액·부가세·합계,
 *    원가·마진 → vox 원가(청구분)·시작 전 원가(우리 부담)·마진·마진율). 마지막 줄 '합계', autofilter 는 머리 줄~마지막 회사 줄.
 *  - 회사별 시트(이름 = 병원 이름, companySheetNames 규칙): 회사 정보 · 정산서 영역(statementSheetModel 출력 그대로,
 *    withNames:false, 원가·마진을 안 고르면 withCost:false) · 원가·마진 영역(costBreakdownModel, withNames:false).
 *  - 금액은 숫자 셀(원 단위) + '#,##0', 마진율은 '0.0%'. 화면 문구('1,234원')를 금액 칸에 넣지 않는다.
 *  - 텍스트 셀은 모두 sanitizeExcelText 를 거친다(수식 시작 문자 막기). 빈칸은 null.
 *  - 저장하지 않은 편집 값은 없다 — 서버 응답만 읽는다.
 *
 * vue·antd·xlsx 를 import 하지 않는다 — 파일 쓰기는 src/utils/settlementExportWorkbook.js 가 한다.
 * node --test 로 검증한다(service/__tests__/settlementExportRows.test.mjs).
 */

import { MANUAL_ONLY_TEXT, costBreakdownModel } from './costBreakdownModel.js';
import { exportContentsText } from './settlementExportSelection.js';
import { EMPTY, formatKstDateTime, formatNumber, isNumber, kstParts } from './settlementFormat.js';
import { unitsMinutesText } from './settlementRules.js';
import { NEEDS_RECALC_TEXT, settlementScopeView } from './settlementStart.js';
import { buildStatementSheet, statementSheetRows } from './statementSheetModel.js';

export const SUMMARY_SHEET_NAME = '요약';
export const SHEET_NAME_MAX = 31;
export const MONEY_FMT = '#,##0';
export const RATE_FMT = '0.0%';
/** 표가 시작하는 행(0 기준) — 1–4행 머리, 5행 비움 */
export const TABLE_START_ROW = 5;

/** 응답 statement_status → 파일 문구 */
export const STATEMENT_STATUS_EXPORT_LABELS = Object.freeze({
  confirmed: '확정',
  review: '검토 중',
  closing: '마감 중',
  failed: '실패',
  estimating: '추정',
  not_billable: '정산 대상 아님',
});
/** 응답 basis → '금액 근거' 문구 */
export const BASIS_LABELS = Object.freeze({
  confirmed_snapshot: '확정 값',
  review_snapshot: '검토 중 저장 값',
  live_estimate: '실시간 추정',
  not_billable: '청구 없음',
});

/** 요약 열 이름 */
export const SUMMARY_HEADERS = Object.freeze({
  base: Object.freeze(['병원', '회사 ID', '정산 범위', '정산 시작일', '정산서 상태', '금액 근거', '내부·테스트']),
  statement: Object.freeze(['공급가액', '부가세', '합계']),
  costMargin: Object.freeze(['vox 원가(청구분)', '시작 전 원가(우리 부담)', '마진', '마진율(일회성 제외 매출 기준)']),
});
export const STATEMENT_AREA_HEADER = Object.freeze(['항목', '사용량', '단가', '금액', '계산값', '메모']);
export const COST_AREA_HEADER = Object.freeze(['구역', '항목', '세부', '사용량', '원가 단가', '원가', '판매 단가', '판매', '마진']);
/** 원가·마진 영역에서 정산서를 고르지 않았을 때 빼는 열 수(판매 단가 · 판매 · 마진) */
const COST_SALE_COLS = 3;
const COST_AREA_LABELS = Object.freeze({ sale: '서티 청구', cost: '원가' });
const CHILD_PREFIX = '  · ';
/** 다시 계산 필요 회사의 '정산 범위' 덧붙임 */
export const NEEDS_RECALC_EXPORT_TEXT = `${NEEDS_RECALC_TEXT}(시작일 반영 전 금액)`;
const DELETED_SUFFIX = ' (삭제)';

/** Excel 이 시트 이름으로 받지 않는 이름(대소문자 무시) */
const RESERVED_SHEET_NAMES = ['history'];
const SHEET_NAME_FORBIDDEN = /[[\]:*?/\\]/g;
// eslint-disable-next-line no-control-regex
const FILE_NAME_FORBIDDEN = /[\\/:*?"<>|\u0000-\u001f]/g;
const FILE_COMPANY_MAX = 60;
const COL_WIDTH_MIN = 6;
const COL_WIDTH_MAX = 50;

const arr = (v) => (Array.isArray(v) ? v : []);
const obj = (v) => (v && typeof v === 'object' && !Array.isArray(v) ? v : null);
const money = (v) => (isNumber(v) ? v : null);
const pad2 = (n) => String(n).padStart(2, '0');

// ---------------------------------------------------------------------------
// 텍스트 · 이름
// ---------------------------------------------------------------------------

/**
 * paymentHistoryExcel.sanitizeForExcel 과 같은 규칙(그 파일은 dayjs·xlsx 를 정적으로 불러 import 하지 않는다):
 * 빈 값은 '', `= + - @ 탭 CR LF` 로 시작하면 앞에 작은따옴표. 이미 막은 값에 다시 써도 같다(멱등).
 * @param {unknown} value
 * @returns {string}
 */
export function sanitizeExcelText(value) {
  if (!value) return '';
  const str = String(value);
  return /^[=+\-@\t\r\n]/.test(str) ? `'${str}` : str;
}

/** 텍스트 셀 — 막고, 비면 null */
function textCell(value) {
  if (value === null || value === undefined) return null;
  const s = sanitizeExcelText(typeof value === 'string' ? value : String(value));
  return s === '' ? null : s;
}

/** 금액 칸 — 숫자면 숫자, 아니면 화면 문구('미정' 등. '—'·빈 문구는 빈칸) */
function moneyOrText(cell) {
  if (isNumber(cell?.value)) return cell.value;
  const t = cell?.text;
  return typeof t === 'string' && t && t !== EMPTY ? textCell(t) : null;
}

const joinText = (parts, sep) => parts.filter((p) => typeof p === 'string' && p).join(sep);

/** UTF-16 길이 max 안에서 글자(코드 포인트)를 자르지 않고 앞에서부터 */
function cutTo(s, max) {
  let out = '';
  for (const ch of s) {
    if (out.length + ch.length > max) break;
    out += ch;
  }
  return out;
}

const stripQuotes = (s) => s.replace(/^'+/, '').replace(/'+$/, '');

/**
 * 회사별 시트 이름(계획서 4-5 — xlsx@0.18.5 는 31자를 넘거나 금지 문자가 있으면 던져 파일 전체가 실패한다)
 *  - `[]:*?/\` 를 빼고 앞뒤 작은따옴표를 뺀다. 비면 회사 ID 끝 6자리.
 *  - 겹침은 소문자로 판정한다('요약'·Excel 예약 이름 'History' 포함 — Excel 은 대소문자를 가리지 않는다).
 *  - 겹치면 ` (2)`·` (3)` … 을 붙이되 접미사 길이만큼 먼저 자른다. 겹치지 않으면 31자로 자른다.
 * @param {Array<{ company_id?: string, company_name?: string }>} companies
 * @returns {string[]} companies 순서 그대로, 길이 같음
 */
export function companySheetNames(companies) {
  const used = new Set([SUMMARY_SHEET_NAME.toLowerCase(), ...RESERVED_SHEET_NAMES]);
  return arr(companies).map((c, i) => {
    let base = stripQuotes(String(c?.company_name ?? '').replace(SHEET_NAME_FORBIDDEN, '').trim()).trim();
    if (!base) base = stripQuotes(String(c?.company_id ?? '').replace(SHEET_NAME_FORBIDDEN, '')).slice(-6);
    if (!base) base = `회사 ${i + 1}`;
    let name = stripQuotes(cutTo(base, SHEET_NAME_MAX));
    for (let n = 2; used.has(name.toLowerCase()); n += 1) {
      const suffix = ` (${n})`;
      name = stripQuotes(cutTo(base, SHEET_NAME_MAX - suffix.length)) + suffix;
    }
    used.add(name.toLowerCase());
    return name;
  });
}

/** 파일 이름에 넣을 글자 — 금지 문자 \/:*?"<>|·제어 문자를 빼고, 끝의 점·빈칸을 뺀다 */
function fileSafe(s, max = FILE_COMPANY_MAX) {
  const cleaned = String(s ?? '')
    .replace(FILE_NAME_FORBIDDEN, '')
    .replace(/\s+/g, ' ')
    .trim();
  return cutTo(cleaned, max).replace(/[.\s]+$/, '');
}

/** 내보낸 시각(ms) — 없으면 응답 generated_at, 그것도 없으면 TypeError */
function exportedAtOf(response, exportedAt) {
  if (isNumber(exportedAt)) return exportedAt;
  const g = response?.generated_at;
  const ms = isNumber(g) ? g : typeof g === 'string' ? Date.parse(g) : NaN;
  if (Number.isFinite(ms)) return ms;
  throw new TypeError('exportedAt(ms) 가 없고 응답 generated_at 도 읽을 수 없다');
}

/**
 * 파일 이름 — '콜봇정산_2026-10_12곳_20261102-1030.xlsx', 1곳이면 '콜봇정산_2026-10_{병원명}_20261102-1030.xlsx'
 * @param {object} response 내보내기 응답
 * @param {{ exportedAt?: number }} [opts] exportedAt: ms(KST 로 YYYYMMDD-HHmm)
 * @returns {string}
 */
export function exportFileName(response, { exportedAt } = {}) {
  const p = kstParts(exportedAtOf(response, exportedAt));
  const stamp = `${p.year}${pad2(p.month)}${pad2(p.day)}-${pad2(p.hour)}${pad2(p.minute)}`;
  const list = arr(response?.companies);
  let who = `${list.length}곳`;
  if (list.length === 1) who = fileSafe(list[0]?.company_name) || fileSafe(list[0]?.company_id) || who;
  return `콜봇정산_${fileSafe(response?.month, 16)}_${who}_${stamp}.xlsx`;
}

// ---------------------------------------------------------------------------
// 시트 만들기
// ---------------------------------------------------------------------------

/** 문자 표시 폭(한글·전각은 2) */
function displayWidth(v) {
  if (v === null || v === undefined) return 0;
  const s = isNumber(v) ? formatNumber(Math.round(v)) : String(v);
  let w = 0;
  for (const ch of s) w += ch.codePointAt(0) > 0x2e80 ? 2 : 1;
  return w;
}

function newSheet(name, head) {
  const aoa = head.map((row) => [...row]);
  const numFmts = [];
  return {
    name,
    aoa,
    numFmts,
    /**
     * 줄 하나 — fmts: { 열 번호: 서식 } (값이 숫자인 칸에만 남긴다)
     * @param {Array<string|number|null>} row
     * @param {Record<number, string>} [fmts]
     * @returns {number} 그 줄의 행 번호(0 기준)
     */
    push(row, fmts = {}) {
      const r = aoa.length;
      aoa.push(row);
      for (const [c, z] of Object.entries(fmts)) {
        if (isNumber(row[Number(c)])) numFmts.push({ r, c: Number(c), z });
      }
      return r;
    },
  };
}

/** 표 부분(6행부터)의 글자 폭으로 열 너비 — 칸 하나뿐인 안내 줄은 옆 칸으로 넘쳐 보이므로 세지 않는다 */
function colsOf(aoa) {
  const width = [];
  aoa.forEach((row, r) => {
    const span = row.filter((v) => v !== null && v !== undefined).length <= 1 && row.length <= 1;
    row.forEach((v, c) => {
      const w = r < TABLE_START_ROW || span ? 0 : displayWidth(v);
      width[c] = Math.max(width[c] ?? 0, w);
    });
  });
  return Array.from(width, (w) => ({ wch: Math.min(COL_WIDTH_MAX, Math.max(COL_WIDTH_MIN, (w ?? 0) + 2)) }));
}

function finish(sheet, autofilter = null) {
  return { name: sheet.name, aoa: sheet.aoa, cols: colsOf(sheet.aoa), numFmts: sheet.numFmts, autofilter };
}

/** 모든 시트 1–4행 + 5행 비움 */
function headRows(response, contents, exportedAtMs) {
  return [
    [textCell(`콜봇 월 정산 ${response?.month ?? ''}`)],
    ['내보낸 시각', `${formatKstDateTime(exportedAtMs)} (KST)`],
    ['내보내기 ID', textCell(response?.export_id)],
    ['담은 내용', textCell(exportContentsText(contents))],
    [],
  ];
}

// ---------------------------------------------------------------------------
// 회사 한 곳 읽기
// ---------------------------------------------------------------------------

/** 회사의 범위 · 상태 · 근거 */
function companyFacts(c) {
  const view = settlementScopeView(c);
  const notBillable = view.scope === 'before_start' || c?.basis === 'not_billable' || c?.statement_status === 'not_billable';
  // 시작일을 저장한 뒤 다시 계산하지 않은 정산서 — 금액이 시작일 반영 전(달 전체) 값이다. 판정 근거(billing_start·warnings)는
  // detail 에 있다(최상위 회사 칸에는 없다)
  const needsRecalc = !notBillable && settlementScopeView(obj(c?.detail) ?? c).needsRecalc;
  const scopeLabel = needsRecalc ? `${view.label} · ${NEEDS_RECALC_EXPORT_TEXT}` : view.label;
  const status = typeof c?.statement_status === 'string' ? c.statement_status : '';
  const basis = typeof c?.basis === 'string' ? c.basis : '';
  return {
    view,
    notBillable,
    needsRecalc,
    scopeLabel,
    name: `${c?.company_name ?? ''}${c?.company_deleted ? DELETED_SUFFIX : ''}`,
    statusText: STATEMENT_STATUS_EXPORT_LABELS[status] ?? status,
    basisText: BASIS_LABELS[basis] ?? basis,
  };
}

/** 마진율 — 분모 margin_revenue(0·null 이거나 margin 이 null 이면 빈칸) */
function marginRate(margin, revenue) {
  return isNumber(margin) && isNumber(revenue) && revenue !== 0 ? margin / revenue : null;
}

// ---------------------------------------------------------------------------
// 요약 시트
// ---------------------------------------------------------------------------

function summarySheet(companies, contents, head) {
  const sheet = newSheet(SUMMARY_SHEET_NAME, head);
  const headers = [
    ...SUMMARY_HEADERS.base,
    ...(contents.statement ? SUMMARY_HEADERS.statement : []),
    ...(contents.cost_margin ? SUMMARY_HEADERS.costMargin : []),
  ];
  const headerRow = sheet.push([...headers]);
  const baseLen = SUMMARY_HEADERS.base.length;
  const moneyCols = [];
  for (let c = baseLen; c < headers.length; c += 1) moneyCols.push(c);
  const rateCol = contents.cost_margin ? headers.length - 1 : -1;
  const fmts = Object.fromEntries(moneyCols.map((c) => [c, c === rateCol ? RATE_FMT : MONEY_FMT]));

  let marginSum = 0;
  let revenueSum = 0;
  let paired = 0;
  let lastRow = headerRow;
  for (const c of companies) {
    const f = companyFacts(c);
    const s = obj(c?.summary) ?? {};
    const row = [
      textCell(f.name),
      textCell(c?.company_id),
      textCell(f.scopeLabel),
      textCell(f.view.startDate),
      textCell(f.statusText),
      textCell(f.basisText),
      c?.internal === true ? '예' : null,
    ];
    if (contents.statement) {
      row.push(...(f.notBillable ? [null, null, null] : [money(s.supply), money(s.vat), money(s.total)]));
    }
    if (contents.cost_margin) {
      const margin = f.notBillable ? null : money(s.margin);
      const revenue = f.notBillable ? null : money(s.margin_revenue);
      row.push(f.notBillable ? null : money(s.vox_cost), money(s.pre_start_cost), margin, marginRate(margin, revenue));
      if (isNumber(margin) && isNumber(revenue)) {
        marginSum += margin;
        revenueSum += revenue;
        paired += 1;
      }
    }
    lastRow = sheet.push(row, fmts);
  }

  // 합계 줄 — 숫자 열의 합(빈칸 제외), 마진율 = 마진 합 / margin_revenue 합(둘 다 있는 회사만, 분모 0 이면 빈칸)
  const total = Array.from({ length: headers.length }, () => null);
  total[0] = '합계';
  for (const col of moneyCols) {
    if (col === rateCol) continue;
    const values = sheet.aoa.slice(headerRow + 1, lastRow + 1).map((r) => r[col]).filter(isNumber);
    total[col] = values.length ? values.reduce((a, b) => a + b, 0) : null;
  }
  if (rateCol >= 0) total[rateCol] = paired ? marginRate(marginSum, revenueSum) : null;
  sheet.push(total, fmts);

  return finish(sheet, { r0: headerRow, c0: 0, r1: lastRow, c1: headers.length - 1 });
}

// ---------------------------------------------------------------------------
// 회사별 시트 — 정산서 영역
// ---------------------------------------------------------------------------

/** 정산 대상 아님 회사의 사용량 요약 한 줄 */
function usageSummaryText(usage) {
  const u = obj(usage) ?? {};
  const calls = obj(u.calls) ?? {};
  const web = isNumber(calls.web) ? calls.web : 0;
  const unitParts = [u.inbound?.units_10s, u.outbound?.units_10s, u.web_voice?.units_10s].filter(isNumber);
  const parts = [];
  if (isNumber(calls.total)) parts.push(`통화 ${formatNumber(calls.total - web)}통`);
  if (web) parts.push(`웹 테스트 ${formatNumber(web)}건`);
  if (unitParts.length) parts.push(`사용 시간 ${unitsMinutesText(unitParts.reduce((a, b) => a + b, 0))}`);
  return parts.length ? `사용량(참고) · ${parts.join(' · ')}` : '';
}

const STATEMENT_FMTS = { 3: MONEY_FMT, 4: MONEY_FMT };
/** 일회성 항목 부모 줄의 파일 보조 문구(화면은 '아래 일회성 항목 목록') */
export const ONE_OFF_FILE_SUB = '건별은 아래 줄';

/** 일회성 항목 — charge.lines.one_off.items, 없으면 statement.lines.one_off.items */
function oneOffItemsOf(detail) {
  const items = detail?.charge?.lines?.one_off?.items ?? detail?.statement?.lines?.one_off?.items;
  return arr(items).filter((it) => obj(it));
}

/**
 * 정산서 영역 — 화면 StatementSheet 의 모델 출력을 대응표대로(계획서 4-5).
 *  부모 줄  [항목, 사용량(· 보조), 단가(VAT 포함·예외 · 보조), 금액, 계산값(수정됨일 때), 메모]
 *  근거 줄  ['  · '+이름, 사용량, 단가, 금액, 빈칸, 설명]
 *  합계 줄  [이름, 빈칸, 참고, 금액, 빈칸, 빈칸] · 안내 줄 [문구]
 */
function pushStatementArea(sheet, c, f, month, contents) {
  const detail = obj(c?.detail) ?? {};
  const model = buildStatementSheet(detail, { month, withNames: false, withCost: contents.cost_margin === true });
  if (f.notBillable) {
    const start = f.view.startDate;
    const text = model.notBillableText || `정산 시작일${start ? `(${start})` : ''} 전 달이라 정산하지 않습니다.`;
    sheet.push([textCell(text)]);
    const usage = usageSummaryText(detail.usage);
    if (usage) sheet.push([textCell(usage)]);
    return;
  }
  sheet.push([...STATEMENT_AREA_HEADER]);
  if (model.notice?.text) sheet.push([textCell(model.notice.text)]);
  for (const row of statementSheetRows(model)) {
    if (row.kind === 'notice') {
      sheet.push([textCell(row.text?.text)]);
    } else if (row.kind === 'item') {
      const unit = joinText([joinText([row.unit?.text, row.unitNote?.text, row.override ? '예외' : ''], ' '), row.unitSub?.text], ' · ');
      const edited = row.edited?.text ? money(row.edited.value) : null;
      const oneOff = row.key === 'one_off' ? oneOffItemsOf(detail) : null;
      // 화면 문구 '아래 일회성 항목 목록'은 화면의 따로 그린 목록(OneOffItems)을 가리킨다 — 파일은 바로 아래 줄에 건별로 편다
      const usageSub = oneOff ? (oneOff.length ? ONE_OFF_FILE_SUB : '') : row.usageSub?.text;
      sheet.push(
        [
          textCell(row.label?.text),
          textCell(joinText([row.usage?.text, usageSub], ' · ')),
          textCell(unit),
          moneyOrText(row.amount),
          edited,
          textCell(row.edited?.text ? row.memo?.text : ''),
        ],
        STATEMENT_FMTS
      );
      for (const it of oneOff ?? []) {
        // 내부·테스트 회사는 항목만 기록하고 청구하지 않는다(부모 줄 0원과 같게)
        const amount = c?.internal === true || detail.internal === true ? 0 : money(it.amount);
        sheet.push([textCell(`${CHILD_PREFIX}${it.name ?? ''}`), null, null, amount, null, textCell(it.memo)], STATEMENT_FMTS);
      }
    } else if (row.kind === 'detail') {
      sheet.push(
        [
          textCell(`${CHILD_PREFIX}${row.name?.text ?? ''}`),
          textCell(row.usage?.text),
          textCell(row.unit?.text ?? ''),
          moneyOrText(row.amount),
          null,
          textCell(row.detail?.text ?? ''),
        ],
        STATEMENT_FMTS
      );
    } else if (row.kind === 'total') {
      sheet.push([textCell(row.label?.text), null, textCell(row.note?.text), money(row.amount?.value), null, null], STATEMENT_FMTS);
    }
  }
}

// ---------------------------------------------------------------------------
// 회사별 시트 — 원가·마진 영역
// ---------------------------------------------------------------------------

/**
 * 원가·마진 영역 — 화면 CostBreakdownTable 의 모델 출력(계획서 4-5).
 *  section 줄 [구역 이름] · detail 줄 [구역, 이름, 문장] · item 줄 [구역, 이름, 세부, 사용량, 원가 단가, 원가, 판매 단가, 판매, 마진]
 *  정산서를 고르지 않았으면(detail 에 charge 없음) 판매 단가 · 판매 · 마진 열을 뺀다.
 */
function pushCostArea(sheet, c, f, month, contents) {
  const detail = obj(c?.detail) ?? {};
  const m = costBreakdownModel(detail, { month, withNames: false });
  const withSale = contents.statement === true;
  const cut = (row) => (withSale ? row : row.slice(0, row.length - COST_SALE_COLS));
  const fmts = withSale ? { 5: MONEY_FMT, 7: MONEY_FMT, 8: MONEY_FMT } : { 5: MONEY_FMT };
  const notBillable = f.notBillable || m.flags?.notBillable === true;

  if (m.flags?.legacy) sheet.push([textCell(m.legacyText)]);
  else if (!m.flags?.priced && withSale && !notBillable) {
    const text = m.flags?.manualOnly ? MANUAL_ONLY_TEXT : m.planMissingText;
    if (text) sheet.push([textCell(text)]);
  }
  sheet.push(cut([...COST_AREA_HEADER]));

  for (const row of arr(m.rows)) {
    const area = COST_AREA_LABELS[row.area] ?? '';
    const name = row.level === 1 ? `${CHILD_PREFIX}${row.name ?? ''}` : row.name ?? '';
    if (row.kind === 'section') {
      sheet.push([textCell(row.name)]);
    } else if (row.kind === 'detail') {
      sheet.push([textCell(area), textCell(name), textCell(joinText([row.sub, row.detail?.text], ' · '))]);
    } else {
      const refNote = row.cost?.ref ? `원가는 참고값(${row.cost.note || '합계 제외'})` : '';
      sheet.push(
        cut([
          textCell(area),
          textCell(name),
          textCell(joinText([row.sub, refNote], ' · ')),
          textCell(row.usage?.text),
          textCell(row.costUnit?.text),
          moneyOrText(row.cost),
          textCell(row.saleUnit?.text),
          moneyOrText(row.sale),
          moneyOrText(row.margin),
        ]),
        fmts
      );
    }
  }

  const t = m.totals ?? {};
  sheet.push(
    cut([
      '합계',
      withSale ? '합계(공급가액)' : 'vox 원가',
      textCell(arr(t.notes).join(' · ')),
      null,
      null,
      money(t.cost?.value),
      null,
      money(t.supply?.value),
      money(t.margin?.value),
    ]),
    fmts
  );
  if (withSale) {
    sheet.push(['합계', '부가세 10%', null, null, null, null, null, money(t.vat?.value), null], fmts);
    sheet.push(['합계', '합계(부가세 포함)', null, null, null, null, null, money(t.totalWithVat?.value), null], fmts);
  }
  if (m.referenceMarginText) sheet.push([textCell(m.referenceMarginText)]);
}

function companySheet(c, name, month, contents, head) {
  const sheet = newSheet(name, head);
  const f = companyFacts(c);
  const scopeText = joinText([f.view.label, f.view.startDate, f.needsRecalc ? NEEDS_RECALC_EXPORT_TEXT : ''], ' · ');
  sheet.push(['병원', textCell(f.name)]);
  sheet.push(['달', textCell(month)]);
  sheet.push(['정산 범위', textCell(scopeText)]);
  sheet.push(['정산서 상태', textCell(f.statusText)]);
  sheet.push(['금액 근거', textCell(f.basisText)]);
  sheet.push([]);
  if (contents.statement) pushStatementArea(sheet, c, f, month, contents);
  if (contents.cost_margin) {
    if (contents.statement) {
      sheet.push([]);
      sheet.push([]);
    }
    pushCostArea(sheet, c, f, month, contents);
  }
  return finish(sheet);
}

// ---------------------------------------------------------------------------
// 응답 → 워크북 데이터
// ---------------------------------------------------------------------------

/**
 * 담은 내용 — opts.contents 를, 없으면 response.contents 를 쓴다. 둘 다 없거나 둘 다 꺼졌으면 TypeError.
 * @returns {{ statement: boolean, cost_margin: boolean }}
 */
function contentsOf(response, contents) {
  const src = obj(contents) ?? obj(response?.contents);
  if (!src) throw new TypeError('buildExportWorkbookData: contents 가 없다(인자·응답 둘 다)');
  const out = { statement: src.statement === true, cost_margin: src.cost_margin === true };
  if (!out.statement && !out.cost_margin) throw new TypeError('buildExportWorkbookData: 담을 내용이 하나도 없다');
  return out;
}

/**
 * 내보내기 응답 → 시트 데이터. 텍스트 셀은 모두 sanitizeExcelText 를 거친 값, 숫자 셀은 number(원 단위), 빈칸은 null.
 * @param {object} response POST /api/v1/callbot/usage/monthly/export 응답
 * @param {{ contents?: { statement: boolean, cost_margin: boolean }, exportedAt?: number }} [opts]
 *   exportedAt: ms — 파일 이름·'내보낸 시각'(KST). 없으면 응답 generated_at.
 * @returns {{ fileName: string, sheets: Array<{ name: string, aoa: Array<Array<string|number|null>>,
 *   cols: Array<{ wch: number }>, numFmts: Array<{ r: number, c: number, z: string }>,
 *   autofilter: { r0: number, c0: number, r1: number, c1: number }|null }> }}
 *   sheets: [요약, 회사별…] — 회사가 하나여도 요약을 둔다.
 */
export function buildExportWorkbookData(response, { contents, exportedAt } = {}) {
  const picked = contentsOf(response, contents);
  const exportedAtMs = exportedAtOf(response, exportedAt);
  const companies = arr(response?.companies);
  const month = typeof response?.month === 'string' ? response.month : '';
  const head = headRows(response, picked, exportedAtMs);
  const names = companySheetNames(companies);
  return {
    fileName: exportFileName(response, { exportedAt: exportedAtMs }),
    sheets: [
      summarySheet(companies, picked, head),
      ...companies.map((c, i) => companySheet(c, names[i], month || c?.detail?.month, picked, head)),
    ],
  };
}
